import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type PollTaskConfig,
    type PollTaskResult,
    type CreateTaskResult,
    type VideoGenerateParams,
} from '../base';
import {
    buildBaiduVodHeaders,
    buildVodQueryString,
    extractFetchTaskMediaId,
    fetchMediaFromUrl,
    getBaiduVodAk,
    getBaiduVodBaseUrl,
    getVodMediaDetail,
    getVodTaskDetail,
    type VodDesubtitleType,
} from './client';
import { getBaiduVodMediaIdFromOptions } from './media-reference';
import { sanitizeSubtitleAreas, type SubtitleArea } from './subtitle-areas';

// 百度智能云 VOD 通道（AK/SK bce-auth-v1 鉴权），当前提供字幕擦除能力。
// 字幕擦除只接受 VOD mediaId：正常链路由 Inngest 前置步骤完成「拉取上传 → mediaId」
// 并经 providerOptions 注入；未注入时（如 playground 直连）在创建前内联准备。

// 内联媒资准备（非 Inngest 链路兜底）：最多等 5 分钟
const INLINE_MEDIA_POLL_INTERVAL_MS = 5000;
const INLINE_MEDIA_MAX_ATTEMPTS = 60;

class BaiduVodProvider extends BaseVideoProvider {
    protected getBaseUrl(): string {
        return getBaiduVodBaseUrl();
    }

    // 鉴权走每请求 AK/SK 签名，context.apiKey 不参与请求；返回 AK 以满足基类非空校验。
    protected getApiKey(): string {
        return getBaiduVodAk();
    }
}

const provider = new BaiduVodProvider({
    baseUrlEnv: 'BAIDU_VOD_BASE_URL',
    apiKeyEnv: 'BAIDU_VOD_AK',
    debugEnv: 'BAIDU_DEBUG',
    providerName: 'baidu-vod',
});

/** 手动选区（源视频像素坐标），由前端首帧框选弹窗写入 modelUi/providerOptions */
function resolveSubtitleAreas(params: VideoGenerateParams): SubtitleArea[] | undefined {
    return sanitizeSubtitleAreas(
        params.modelUi?.subtitle_areas ?? (params.providerOptions as any)?.subtitle_areas,
    );
}

function resolveSubtitleType(
    params: VideoGenerateParams,
    hasAreas: boolean,
): VodDesubtitleType {
    const raw = String(
        params.modelUi?.subtitle_type ?? (params.providerOptions as any)?.subtitle_type ?? '',
    ).toLowerCase();
    // manual 必须携带 areaList；无有效选区时回退对白擦除（与 ui-options 的回退一致）
    if (raw === 'manual') return hasAreas ? 'manual' : 'dialog';
    return raw === 'global' ? 'global' : 'dialog';
}

function resolveModelType(params: VideoGenerateParams): 'v3' | 'v4' {
    const raw = String(
        params.modelUi?.desubtitle_model ?? (params.providerOptions as any)?.desubtitle_model ?? '',
    ).toLowerCase();
    // v3 默认；v4 效果更好但耗时更长
    return raw === 'v4' ? 'v4' : 'v3';
}

/** 非 Inngest 链路兜底：内联完成拉取上传并等待媒资可用。 */
async function ensureVodMediaInline(sourceUrl: string): Promise<string> {
    const { taskId } = await fetchMediaFromUrl({ url: sourceUrl });

    for (let attempt = 0; attempt < INLINE_MEDIA_MAX_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, INLINE_MEDIA_POLL_INTERVAL_MS));

        const task = await getVodTaskDetail(taskId);
        const taskStatus = String(task.status || '').toUpperCase();
        if (taskStatus === 'FAILED') {
            throw new Error('Baidu VOD fetch task failed');
        }

        const mediaId = extractFetchTaskMediaId(task);
        if (mediaId) {
            // v2 媒资详情无 PUBLISHED 状态字段，拉取成功即源文件就绪，只兜底封禁态
            const media = await getVodMediaDetail(mediaId);
            const banStatus = String(media.banStatus || '').toUpperCase();
            if (banStatus === 'BAN' || banStatus === 'BANNED') {
                throw new Error('Baidu VOD media is banned');
            }
            return mediaId;
        }
        if (taskStatus === 'SUCCESS' || taskStatus === 'FINISHED') {
            throw new Error(`Baidu VOD fetch task ${taskStatus.toLowerCase()} without mediaId`);
        }
    }
    throw new Error('Timeout waiting for Baidu VOD media to become available');
}

const adapter: VideoProviderAdapter = {
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        const sourceUrl = (params.videoUri || '').trim();
        if (!sourceUrl) {
            throw new Error('Baidu VOD desubtitle requires an input video');
        }

        const mediaId =
            getBaiduVodMediaIdFromOptions(params.providerOptions, sourceUrl) ||
            (await ensureVodMediaInline(sourceUrl));

        const areas = resolveSubtitleAreas(params);
        const subtitleType = resolveSubtitleType(params, Boolean(areas));

        const path = '/v2/desubtitle';
        return {
            url: `${context.baseUrl}${path}`,
            method: 'POST',
            headers: buildBaiduVodHeaders({ method: 'POST', path }),
            body: {
                mediaId,
                subtitleType,
                modelType: resolveModelType(params),
                // manual: 区域内全擦除，areaList 为源视频像素坐标
                ...(subtitleType === 'manual' && areas
                    ? { desubtitleParams: [{ areaList: areas }] }
                    : {}),
            },
        };
    },

    parseCreateTaskResponse(modelId, response, data): CreateTaskResult {
        const taskId = data?.taskId;
        if (!taskId) {
            throw new Error('Failed to get task id from Baidu VOD desubtitle response');
        }
        return { taskId, raw: data };
    },

    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        const path = '/v2/desubtitle/list';
        const query = { taskId };
        return {
            url: `${context.baseUrl}${path}?${buildVodQueryString(query)}`,
            method: 'GET',
            headers: buildBaiduVodHeaders({ method: 'GET', path, query }),
        };
    },

    parsePollTaskResponse(modelId, response, data): PollTaskResult {
        const list = Array.isArray(data?.data) ? data.data : [];
        const entry = list[0];
        // 任务刚创建可能查不到记录，按处理中兜底（外层有总超时）
        if (!entry) {
            return { status: TaskStatus.PROCESSING, raw: data };
        }

        const state = String(entry.status || '').toUpperCase();

        // 文档写终态是 FINISHED，实测返回 SUCCESS，两者都按成功处理
        if (state === 'SUCCESS' || state === 'FINISHED') {
            const videoUrl = entry.targetUrl;
            if (!videoUrl) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing target URL in desubtitle result',
                    raw: data,
                };
            }
            return { status: TaskStatus.SUCCESS, videoUrl, raw: data };
        }

        // 文档仅列出 READY/RUNNING/FINISHED；FAILED 等异常态按失败处理
        if (state === 'FAILED' || state === 'FAILURE' || state === 'ERROR') {
            return {
                status: TaskStatus.FAILED,
                error: String(entry.errMsg || entry.message || `Desubtitle task ${state.toLowerCase()}`),
                raw: data,
            };
        }

        return { status: TaskStatus.PROCESSING, raw: data };
    },
};

export const baiduVod = (modelId: string): VideoModel =>
    provider.createVideoModel(modelId, adapter);
